import { create } from 'zustand'
import type { QuotaError } from '@/@types/trackheros'

/**
 * Billing UI state that has to live above the route tree.
 *
 * UI state only, never fetched data (frontend CLAUDE.md §4) — the entitlement
 * itself lives in SWR under the billing summary. This holds two things:
 *
 * - the last quota refusal the API returned (DEV-87), so a top-level dialog
 *   can explain it. Populated by the axios error interceptor, which **still
 *   rejects the promise** after publishing here. A 402 is not a global
 *   redirect: the client-create form wants to show the message inline next to
 *   the field, and a caller that never saw the error could not.
 * - whether the plan dialog is open. It is opened from the drawer's plan item,
 *   and the mobile drawer unmounts its contents the moment it closes — so the
 *   dialog is mounted once in `Layouts` and opened through here, not rendered
 *   inside the menu that opens it.
 *
 * Reset from `clearSession()`: a module-level store outlives sign-out, and a
 * dialog that reopens on the next user's session showing the previous user's
 * plan limits is the leak §7 warns about.
 */

type BillingUiState = {
    quotaError: QuotaError | null
    showQuotaError: (error: QuotaError) => void
    dismissQuotaError: () => void
    planDialogOpen: boolean
    openPlanDialog: () => void
    closePlanDialog: () => void
    reset: () => void
}

export const useBillingStore = create<BillingUiState>((set) => ({
    quotaError: null,
    showQuotaError: (error) => set({ quotaError: error }),
    dismissQuotaError: () => set({ quotaError: null }),
    planDialogOpen: false,
    openPlanDialog: () => set({ planDialogOpen: true }),
    closePlanDialog: () => set({ planDialogOpen: false }),
    reset: () => set({ quotaError: null, planDialogOpen: false }),
}))

/** Called from `clearSession()`. See the note above. */
export const resetBillingUi = () => useBillingStore.getState().reset()
